import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { shippedApps } from "@/data/products";
import VlrLogo from "@/components/VlrLogo";

export default function Footer() {
  const { t, lang, setLang, localizePath } = useLang();

  const groups = [
    {
      title: t("footer.products"),
      links: shippedApps.map((p) => ({ label: p.name, to: `/app/${p.slug}` })),
    },
    {
      title: t("footer.company"),
      links: [
        { label: t("footer.about"), to: "/about" },
        { label: t("footer.enterprise"), to: "/enterprise" },
        { label: t("footer.press"), to: "/press" },
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        { label: t("footer.privacy"), to: "/privacy" },
        { label: t("footer.terms"), to: "/terms" },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-ink/10 bg-paper">
      <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-20">
        <div className="flex flex-col gap-12 md:flex-row md:justify-between">
          <div className="max-w-xs">
            <div className="flex items-center gap-3">
              <VlrLogo className="h-10 w-10 text-ink" />
              <span className="text-xl font-semibold tracking-tight">VLR Software LLC</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-ink/60">{t("footer.tagline")}</p>
          </div>

          <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:grid-cols-5">
            {groups.map((g) => (
              <div key={g.title}>
                <h3 className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{g.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {g.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={localizePath(l.to)}
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9áéíóú]+/g, "-")}`}
                        className="text-sm text-ink/70 transition-colors hover:text-cobalt"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div>
              <h3 className="text-[11px] font-medium uppercase tracking-[0.25em] text-ink/40">{t("footer.contact")}</h3>
              <ul className="mt-4 space-y-2.5">
                <li>
                  <a
                    href="mailto:site@vlrsoftware.com"
                    data-testid="footer-link-email"
                    className="group inline-flex items-center gap-1 text-sm text-ink/70 transition-colors hover:text-cobalt"
                  >
                    {t("footer.emailUs")}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-ink/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-ink/40">{t("footer.rights")}</p>
          <div className="flex items-center gap-3" data-testid="language-toggle">
            <span className="text-[11px] uppercase tracking-[0.25em] text-ink/40">{t("footer.language")}</span>
            {["en", "es"].map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`lang-toggle-${code}`}
                aria-pressed={lang === code}
                onClick={() => setLang(code)}
                className={`border-b pb-0.5 text-sm font-medium uppercase tracking-wider transition-colors ${
                  lang === code ? "border-cobalt text-ink" : "border-transparent text-ink/40 hover:text-ink"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
